import React, { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";

import cameras, { fullSpecCameras, exposureOnlyCameras } from "../data/cameras";
import scenarios from "../data/scenarios.json";
import lightingConditions from "../data/lighting.json";
import movements from "../data/movement.json";
import looks from "../data/looks.json";
import outputs from "../data/output.json";

import { generateRecipe } from "../engine/ruleEngine";
import { generateExplanation } from "../engine/explanationEngine";
import { generateDelivery } from "../engine/deliveryEngine";
import Footer from "../components/Footer";
import ShareButton from "../components/ShareButton";
import Icon, { SETTING_ICONS } from "../components/Icon";
import MeasurePrompt from "../components/MeasurePrompt";
import { coverageNote } from "../utils/cameraFacts";
import { setPageMeta } from "../utils/meta";



function Field({ label, hint, children }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

function SettingTile({ icon, label, value, wide = false }) {
  return (
    <div className={`tile ${wide ? "tile-wide" : ""}`}>
      <span className="tile-label">
        <Icon name={SETTING_ICONS[icon] || "aperture"} size={14} />
        {label}
      </span>
      <span className="tile-value data">{value}</span>
    </div>
  );
}

/**
 * The builder is no longer gated.
 *
 * It used to sit entirely behind the access code, which had three
 * problems. The prerenderer captured the LOCKED state, so the static HTML
 * submitted to search engines was a lock screen. Roughly 290 internal
 * links — every reference page, plus home, gallery and the footer — pointed
 * at that wall. And the gate protected a string that ships in the bundle
 * next to the engine it was protecting.
 *
 * The rule the site now follows in both places: calculated answers are
 * free, measured readings are gated. The builder calculates, so it's open.
 * The on-device analyser on a scanned page measures, so it isn't.
 *
 * State lives in the query string, which makes a configured recipe
 * shareable and bookmarkable — the closest a dynamic tool gets to being
 * linkable, and the thing that was actually missing.
 */
export default function Builder() {
  const [searchParams, setSearchParams] = useSearchParams();

  const pick = (key, list, fallback) => {
    const v = searchParams.get(key);
    return v && list.some((x) => x.id === v) ? v : fallback;
  };

  const cameraId = pick("camera", cameras, fullSpecCameras[0].id);
  const scenarioId = pick("scenario", scenarios, scenarios[0].id);
  const lightingId = pick("lighting", lightingConditions, lightingConditions[0].id);
  const lookId = pick("look", looks, looks[0].id);
  const outputId = pick("output", outputs, "youtube-long");
  const level = searchParams.get("level") === "beginner" ? "beginner" : "advanced";

  const update = (key, value) => {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const camera = cameras.find((c) => c.id === cameraId) || fullSpecCameras[0];
  const scenario = scenarios.find((s) => s.id === scenarioId) || scenarios[0];
  const lighting = lightingConditions.find((l) => l.id === lightingId) || lightingConditions[0];
  const movement = movements.find((m) => m.id === scenario.movementId);
  const look = looks.find((l) => l.id === lookId) || looks[0];
  const output = outputs.find((o) => o.id === outputId) || outputs[0];

  const { recipe, explanation, delivery } = useMemo(() => {
    const r = generateRecipe({ camera, scenario, lighting, movement, look, output, experienceLevel: level });
    return {
      recipe: r,
      explanation: generateExplanation(r, { camera, scenario, lighting }),
      delivery: generateDelivery(r, output, scenario),
    };
  }, [camera, scenario, lighting, movement, look, output, level]);

  useEffect(() => {
    setPageMeta({
      title: "Recipe builder, settings for any camera and condition",
      description: "Generate camera settings for any camera, scenario and lighting combination, including the 109 cameras without published pages. Same engine, run live.",
      path: "/builder/",
    });
  }, []);

  const s = recipe.settings;

  return (
    <main className="builder-page">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link> <span aria-hidden="true">/</span> <span>Builder</span>
      </nav>

      <header className="builder-header">
        <p className="eyebrow">Runs on all {cameras.length} cameras</p>
        <h1>Recipe builder</h1>
        <p className="builder-sub">
          The published pages cover the combinations people search for. This runs the same engine on
          any of them, including the {exposureOnlyCameras.length} cameras that have exposure data
          but no page of their own.
        </p>
      </header>

      <div className="builder-layout">
        {/* Controls stay put while the results scroll, on a wide screen
            you're changing one input and watching what moves, and a panel
            that scrolls away makes that a scroll-up-scroll-down loop. */}
        <aside className="builder-controls">
          <div className="controls-inner">
            <h2 className="controls-heading">Your shot</h2>

            <Field label="Camera">
              <select value={cameraId} onChange={(e) => update("camera", e.target.value)}>
                <optgroup label="Full specs on file">
                  {fullSpecCameras.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </optgroup>
                <optgroup label="Exposure data only">
                  {exposureOnlyCameras.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </optgroup>
              </select>
            </Field>

            <Field label="Scenario" hint={scenario.description}>
              <select value={scenarioId} onChange={(e) => update("scenario", e.target.value)}>
                {Object.entries(
                  scenarios.reduce((acc, sc) => {
                    (acc[sc.category] = acc[sc.category] || []).push(sc);
                    return acc;
                  }, {})
                ).map(([category, list]) => (
                  <optgroup key={category} label={category}>
                    {list.map((sc) => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
                  </optgroup>
                ))}
              </select>
            </Field>

            <Field label="Lighting">
              <select value={lightingId} onChange={(e) => update("lighting", e.target.value)}>
                {lightingConditions.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </Field>

            <Field label="Look">
              <select value={lookId} onChange={(e) => update("look", e.target.value)}>
                {looks.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </Field>

            <Field label="Delivering to">
              <select value={outputId} onChange={(e) => update("output", e.target.value)}>
                {outputs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </Field>

            <Field
              label="Grading"
              hint={level === "beginner" ? "Keeps you on profiles that need no grade." : "Allows flat profiles that need grading."}
            >
              <select value={level} onChange={(e) => update("level", e.target.value)}>
                <option value="advanced">I'll grade the footage</option>
                <option value="beginner">Straight out of camera</option>
              </select>
            </Field>

            <div className="controls-share">
              <ShareButton
                title={`${camera.name}, ${scenario.name} settings`}
                text={`${scenario.name} on the ${camera.name} in ${lighting.name.toLowerCase()}`}
                url={typeof window !== "undefined" ? window.location.href : ""}
              />
              <p className="controls-share-note">
                Every choice is in the address bar, so this link reopens exactly this setup.
              </p>
            </div>
          </div>
        </aside>

        <div className="builder-result">
          <div className="result-head">
            <div>
              <p className="eyebrow">{camera.name}</p>
              <h2>{scenario.name} in {lighting.name}</h2>
            </div>
            <span className={`confidence-pill conf-${explanation.confidenceTone}`}>
              <span className="conf-dot" aria-hidden="true" />
              {explanation.confidenceLabel}
            </span>
          </div>

          {coverageNote(camera) && <p className="result-coverage">{coverageNote(camera)}</p>}

          {/* A grid rather than a table: on a settings readout the eye is
              hunting one value, and tiles let it land on a number instead
              of tracking along a row. */}
          <div className="tile-grid">
            <SettingTile icon="resolution" label="Resolution" value={s.resolution} wide />
            <SettingTile icon="fps" label="Frame rate" value={`${s.fps}fps`} />
            <SettingTile icon="shutter" label="Shutter" value={s.shutter} />
            <SettingTile icon="iso" label="ISO" value={s.iso} />
            <SettingTile icon="ndFilter" label="ND filter" value={s.ndFilter} />
            <SettingTile icon="colorTemperature" label="White balance" value={s.colorTemperature} />
            <SettingTile icon="profile" label="Profile" value={s.profile} />
            <SettingTile icon="sharpness" label="Sharpness" value={s.sharpness} />
            <SettingTile icon="contrast" label="Contrast" value={s.contrast} />
            <SettingTile icon="saturation" label="Saturation" value={s.saturation} />
            <SettingTile icon="stabilization" label="Stabilization" value={s.stabilization} wide />
            <SettingTile icon="focus" label="Focus" value={s.focus} wide />
          </div>

          {s.ndNote && <p className="result-note">{s.ndNote}</p>}

          {explanation.confidenceReasons.length > 0 && (
            <details className="result-assumptions">
              <summary>
                What this recipe assumed
                <span className="assumption-count data">{explanation.confidenceReasons.length}</span>
              </summary>
              <ul>
                {explanation.confidenceReasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </details>
          )}

          <MeasurePrompt recipe={recipe} lighting={lighting} camera={camera} />

          <section className="editor-setup" aria-labelledby="editor-heading">
            <h3 id="editor-heading">Setting up the edit</h3>
            <p className="editor-lead">{delivery.editorSetup.lead}</p>
            <p className="editor-space">
              <span className="eyebrow">Input colour space</span>
              <span className="data">{delivery.editorSetup.space}</span>
            </p>
            <dl className="editor-list">
              {delivery.editorSetup.editors.map((e) => (
                <div className="editor-item" key={e.name}>
                  <dt>{e.name}</dt>
                  <dd>{e.body}</dd>
                </div>
              ))}
            </dl>
            {delivery.editorSetup.note && <p className="editor-note">{delivery.editorSetup.note}</p>}
          </section>

          <section className="delivery" aria-labelledby="delivery-heading">
            <h3 id="delivery-heading">Getting it to {output.name}</h3>
            <ol className="delivery-steps">
              {delivery.steps.map((step, i) => (
                <li key={i}>
                  <strong>{step.title}</strong>
                  <p>{step.body}</p>
                </li>
              ))}
            </ol>
            <p className="delivery-note">{delivery.numbersNote}</p>
          </section>

          <section className="result-why" aria-labelledby="why-heading">
            <h3 id="why-heading">Why these settings</h3>
            {explanation.whyBlocks.map((b, i) => (
              <details className="why-item" key={i}>
                <summary>{b.title}</summary>
                <p>{b.text}</p>
              </details>
            ))}
          </section>
        </div>
      </div>

      <div data-prerender-ready="true" style={{ display: "none" }} />
      <Footer />
    </main>
  );
}
